import { type FC, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import CustomModalHeader from "~/features/scanDmc/components/CustomModalHeader/CustomModalHeader";
import { Box, Button, CircularProgress, Stack, styled, TextField, Typography } from "~/common/mui";
import { BackLink } from "~/common/components/Link";
import Modal from "~/common/components/Modal";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { countTotalSize, FileUpload, MaxSizeError } from "~/features/scanDmc/components/FileUpload/FileUpload";
import ScanResultAlert from "~/features/scanDmc/components/ScanResultAlert/ScanResultAlert";
import { useTranslation } from "react-i18next";
import { type ScanResultType } from "~/features/scanDmc/types/ScanDMC";
import type { ContactFormData } from "~/features/scanDmc/api/apiSendDMCContactForm";
import { apiSendDMCContactForm } from "~/features/scanDmc/api/apiSendDMCContactForm";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { EMPTY_TITLE_CODE } from "~/features/scanDmc/components/ContactFormOverlay/dmcContactFormUtils";
import { useSalutationOptions, useTitleOptions } from "~/domains/referenceData/hooks/useFormOptions";
import { trackDmcForm } from "~/app/analytics/ga/ga";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { validateEmail } from "~/utils/emailValidator";
import { useSessionUser } from "~/app/config/useSession";

const TextAreaLabel = styled("label")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(1),
  "& > span": {
    color: theme.palette.text.secondary,
  },
  "& input": {
    resize: "none",
  },
}));

const MAX_FILE_SIZE = 10_000_000; // 10MB

interface ContactFormOverlayProps {
  showAlert?: boolean;
  onClose: () => void;
  scanResult: ScanResultType;
  dmcCode: string;
}

type Step = "form" | "sending" | "sendResult";

const ContactFormOverlay: FC<ContactFormOverlayProps> = ({ showAlert, onClose, scanResult, dmcCode }) => {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const initialValues = useMemo<ContactFormData>(() => {
    const base: ContactFormData = {
      titleCode: EMPTY_TITLE_CODE,
      salutationCode: "",
      firstName: "",
      lastName: "",
      email: "",
      description: "",
      dmcCode: "",
      files: [],
    };
    if (!userIsLoggedIn() || !sessionUser) {
      return base;
    }
    return {
      ...base,
      firstName: sessionUser.firstName ?? "",
      lastName: sessionUser.lastName ?? "",
      titleCode: sessionUser.titleCode ?? EMPTY_TITLE_CODE,
      salutationCode: sessionUser.salutationCode ?? "",
      email: sessionUser.email ?? "",
    };
  }, [sessionUser]);
  const { control, register, handleSubmit, reset } = useForm({ defaultValues: { ...initialValues, dmcCode } });

  useEffect(() => {
    reset({ ...initialValues, dmcCode });
  }, [dmcCode, initialValues, reset]);

  const [step, setStep] = useState<Step>("form");

  const onSubmit = async (data: ContactFormData) => {
    setStep("sending");
    const result = await apiSendDMCContactForm(data);
    setStep("sendResult");
    if (import.meta.env.DEV) {
      if (!result) {
        console.error("Error when sending contact form");
      }
    }
    trackDmcForm(scanResult, dmcCode, result);
  };

  const titleOptions = useTitleOptions();
  const salutationOptions = useSalutationOptions();

  const showSpinner = step === "sending";
  const form = (
    <Box sx={{ position: "relative" }}>
      <BackLink className="mb-24" onClick={onClose}>
        {t("page.scandmc.back")}
      </BackLink>
      <Stack sx={{ gap: 2 }}>
        {showAlert && <ScanResultAlert scanResult={scanResult} />}
        <Controller
          name={"titleCode"}
          control={control}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <SimpleSelect
              inputRef={ref}
              fullWidth
              values={titleOptions}
              label={t("page.scandmc.form.title")}
              error={!!error}
              {...inputProps}
            />
          )}
        />

        <Controller
          name={"salutationCode"}
          control={control}
          rules={{ required: t("page.scandmc.form.error.required") }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <SimpleSelect
              inputRef={ref}
              fullWidth
              values={salutationOptions}
              label={t("page.scandmc.form.salutation") + "*"}
              error={!!error}
              helperText={error?.message ?? null}
              {...inputProps}
            />
          )}
        />

        <Controller
          name={"firstName"}
          control={control}
          rules={{ required: t("page.scandmc.form.error.required") }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <TextField
              fullWidth
              label={t("page.scandmc.form.firstName") + "*"}
              error={!!error}
              inputRef={ref}
              {...inputProps}
              helperText={error?.message ?? null}
            />
          )}
        />
        <Controller
          name={"lastName"}
          control={control}
          rules={{ required: t("page.scandmc.form.error.required") }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <TextField
              fullWidth
              label={t("page.scandmc.form.lastName") + "*"}
              error={!!error}
              inputRef={ref}
              {...inputProps}
              helperText={error?.message ?? null}
            />
          )}
        />
        <Controller
          name={"email"}
          control={control}
          rules={{
            validate: validateEmail(
              t("page.scandmc.form.error.required"),
              t("page.contactUs.form.error.email.pattern"),
            ),
          }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <TextField
              fullWidth
              error={!!error}
              label={t("page.scandmc.form.email") + "*"}
              inputRef={ref}
              {...inputProps}
              helperText={error?.message ?? null}
            />
          )}
        />
        <TextAreaLabel>
          <span>{t("page.scandmc.form.tellUsMore")}</span>
          <Controller
            name={"description"}
            control={control}
            render={({ field: { ref, ...inputProps } }) => (
              <TextField fullWidth inputRef={ref} {...inputProps} multiline minRows={4} helperText={null} />
            )}
          />
        </TextAreaLabel>

        <Controller
          name={"files"}
          control={control}
          rules={{
            validate: (files) => countTotalSize(files) <= MAX_FILE_SIZE,
          }}
          render={({ field, fieldState: { error } }) => (
            <FileUpload
              {...field}
              multiple
              error={error && <MaxSizeError maxSize={MAX_FILE_SIZE} />}
              disabled={!!error}
            />
          )}
        />
      </Stack>
      {showSpinner && <CircularProgress sx={{ position: "fixed", inset: 0, margin: "auto" }} size={64} />}
    </Box>
  );

  const sentScreen = (
    <Box
      sx={{
        mt: "200px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "25px",
      }}
    >
      <div>
        <CheckCircleIcon sx={{ fontSize: 64 }} />
      </div>
      <Typography sx={{ textAlign: "center", whiteSpace: "pre-line" }}>
        <p>{t("page.scandmc.form.thankYou")}</p>
      </Typography>
    </Box>
  );

  const footerButton =
    step === "form" ? (
      <Button onClick={() => handleSubmit(onSubmit)()} fullWidth>
        {t("page.scandmc.form.button.send")}
      </Button>
    ) : (
      <Button onClick={onClose} fullWidth>
        {t("page.scandmc.form.button.close")}
      </Button>
    );

  const showForm = step === "form" || step === "sending";
  const showResultScreen = step === "sendResult";

  return (
    <div>
      <Modal
        headerOverride={<CustomModalHeader />}
        children={
          <>
            {showForm && form}
            {showResultScreen && sentScreen}
          </>
        }
        footerChildren={footerButton}
        modalClassName="scha__modal--scanner"
        onClose={onClose}
      />
    </div>
  );
};

export default ContactFormOverlay;
